import React, { forwardRef, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import SvgGreenCheckBadge from 'src/assets/green-check-mark-badge';
import { PurpleBoltIconSmall } from 'src/assets/purple-bolt-icon-small';
import { getColors } from 'src/theme';
import { Typography } from 'src/typography';
import { ModalContainer as _ModalContainer, ModalExitIcon } from 'components/core';
import { useKioskSelectPayment } from './use-kiosk-select-payment';
import OrderItems from '../order-items';

import { PbbQrCode } from './pbb-qr-code';
import { NonPbbOptions } from './non-pbb-options';

const themeColors = getColors();
const BANNER_TIMEOUT = 10000; // 10 seconds

export const SelectPaymentModal = forwardRef<HTMLDivElement, { onClose: () => void }>((props, ref) => {
  const { onClose } = props;
  const [showBanner, setShowBanner] = useState(true);
  const [isPBB, setIsPBB] = useState(true);
  const {
    counterPaymentOptions,
    dispensary,
    error,
    handleOnClose,
    handleOnErrorClose,
    handleUpdateOrderPayment,
    instoreCartUrl,
    loading,
    onPaymentSelect,
    pbbOption,
  } = useKioskSelectPayment({
    onClose,
  });

  const pbbEnabled = !!pbbOption && !pbbOption.disabled;
  const showTabs = pbbEnabled && counterPaymentOptions.length > 0;

  const handlePaymentSelect = (): void => {
    setIsPBB(!isPBB);
    const paymentOption = isPBB ? counterPaymentOptions[0].value : 'dutchiePay';
    onPaymentSelect(paymentOption);
  };

  useEffect(() => {
    setIsPBB(pbbEnabled);
  }, [pbbEnabled]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowBanner(false);
    }, BANNER_TIMEOUT);

    return () => clearTimeout(timer);
  }, []);

  return (
    <ModalContainer ref={ref} {...props}>
      <Wrapper>
        <Banner showBanner={showBanner}>
          <SvgGreenCheckBadge color={themeColors.green90} />
          <Typography.Body size='small' tag='p'>
            <strong>Order submitted!</strong> It will be ready momentarily.
          </Typography.Body>
        </Banner>
        <ModalExitIcon onClick={handleOnClose} />
        {!error && (
          <ModalHeading size='large' tag='h2'>
            How do you want to pay?
          </ModalHeading>
        )}
        {showTabs && (
          <ButtonContainer>
            <PaymentButton onClick={handlePaymentSelect} active={isPBB}>
              <span className='payment-type'>Pay now</span>
              <Badge>
                {pbbOption.sublabel ? (
                  <>
                    <PurpleBoltIconSmall />
                    Faster
                  </>
                ) : (
                  <>$0 Fee</>
                )}
              </Badge>
            </PaymentButton>
            <PaymentButton onClick={handlePaymentSelect} active={!isPBB}>
              <span className='payment-type'>Pay at counter</span>
            </PaymentButton>
          </ButtonContainer>
        )}
        {pbbEnabled && isPBB && (
          <PbbQrCode
            handleOnErrorClose={handleOnErrorClose}
            instoreCartUrl={instoreCartUrl}
            error={error}
            loading={loading}
          />
        )}
        {!isPBB && (
          <NonPbbOptions
            counterPaymentOptions={counterPaymentOptions}
            error={error}
            onPaymentSelect={onPaymentSelect}
            handleOnErrorClose={handleOnErrorClose}
            handleUpdateOrderPayment={handleUpdateOrderPayment}
          />
        )}
      </Wrapper>
      {!error && <OrderItems dispensary={dispensary} loading={loading} />}
    </ModalContainer>
  );
});

const Wrapper = styled.div`
  ${({ theme: { spaces } }) => css`
    padding: ${spaces[9]};
  `}
`;

const ModalContainer = styled(_ModalContainer)`
  ${({ theme: { colors, spaces } }) => css`
    position: relative;
    padding: 0;
    min-width: 640px;
    max-width: 820px;
    background-color: ${colors.grey[95]};
    overflow: hidden;

    @media screen and (max-width: 900px) {
      margin: 0 ${spaces[4]};
    }
  `}
`;

const Banner = styled.div<{ showBanner: boolean }>`
  ${({ theme: { colors, spaces }, showBanner }) => css`
    background-color: ${colors.green20};
    border-radius: 8px;
    padding: ${spaces[4]} ${spaces[6]} ${spaces[4]} ${spaces[6]};
    display: flex;
    align-items: center;
    position: absolute;
    top: -80px;
    left: 0;
    width: 100%;
    max-width: 850px;
    gap: ${spaces[2]};
    opacity: ${showBanner ? 1 : 0};
    overflow: hidden;
    transition: opacity 0.3s ease-in-out;
  `}
`;

const ModalHeading = styled(Typography.Heading)`
  ${({ theme: { spaces } }) => css`
    margin-bottom: ${spaces[9]};
  `}
`;

const ButtonContainer = styled.div`
  ${({ theme: { spaces } }) => css`
    display: flex;
    gap: ${spaces[6]};
    margin-bottom: ${spaces[6]};
  `}
`;

const PaymentButton = styled.button`
  ${({ active, theme: { colors, customized, spaces } }) => css`
    background-color: ${colors.white};
    width: 100%;
    height: 88px;
    padding: ${spaces[4]} ${spaces[6]};
    font-size: 24px;
    font-weight: 400;
    color: ${colors.primaryBlack};
    border-radius: 16px;
    border: 4px solid ${active ? customized.colors.buttonsLinks : 'transparent'};
    cursor: pointer;

    .payment-type {
      font-weight: ${active ? 700 : 400};
    }
  `}
`;

const Badge = styled.div`
  ${({ theme: { colors, spaces } }) => css`
    align-items: center;
    background: ${colors.purple10};
    border-radius: 14px;
    color: ${colors.purple100};
    display: inline-flex;
    font-size: 12px;
    font-weight: 600;
    gap: ${spaces[1]};
    margin-left: ${spaces[2]};
    letter-spacing: 0.5px;
    line-height: 1;
    padding: 6px ${spaces[3]};
    position: relative;
    top: -2px;
  `}
`;
